import { Plus, AlertCircle, Boxes } from 'lucide-react'
import { projectsPageTabs, tasksPageTabs } from './tabs'
import type { ProjectsFiltersState } from '@/components/projects'
import { GroupingSelector } from '@/components/queries/grouping'
import type { TasksFiltersState } from '@/components/tasks'
import { MessageInbox, type MessagesFiltersState } from '@/components/messages'
import { NodeList } from '@/components/nodes'
import { DOCUMENT_GROUPING_DEFAULTS } from '@/lib/config/node-grouping-configs'
import type { DocumentFilters } from '@/components/documents'
import { NodeGrouping } from '@/components/nodes'
import { Grouping } from '@/components/queries/grouping'
import { GROUPING_CONFIGS } from '@/lib/config/node-grouping-configs'
import { useAuth } from '@/hooks/useAuth'
import { useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import type { Message } from '@/types'
import { MESSAGE_GROUPING_DEFAULTS } from '@/lib/config/node-grouping-configs'
import type { WorkspacesFiltersState } from '@/components/workspaces'
import {
  type ProjectGroupByOption,
  type MessageGroupByOption,
  type DocumentGroupByOption,
  type WorkspaceGroupByOption,
  type TaskGroupByOption,
} from '@/lib/config/node-grouping-configs'
import { BaseFiltersContainer } from '@/components/queries/BaseFiltersContainer'
import {
  WORKSPACES_FILTER_CONFIGS,
  PROJECTS_FILTER_CONFIGS,
  TASKS_FILTER_CONFIGS,
  MESSAGES_FILTER_CONFIGS,
  DOCUMENT_FILTER_CONFIGS,
} from '@/lib/config/node-filters-configs'
import { documentsPageTabs } from './tabs'
import { Project, Task, User, Workspace, Document } from '@/types'
import type { NodeListConfig } from '@/components/nodes/NodeListPage'

// Message grouping options and labels
const MESSAGE_GROUPING_OPTIONS: MessageGroupByOption[] = [
  'none',
  'status',
  'creator',
  'workspace',
  'messageType',
  'readStatus',
  'priority',
]

const MESSAGE_GROUPING_OPTION_LABELS: Record<MessageGroupByOption, string> = {
  none: 'No Grouping',
  status: 'Group by Status',
  creator: 'Group by Creator',
  workspace: 'Group by Workspace',
  messageType: 'Group by Type',
  readStatus: 'Group by Read Status',
  priority: 'Group by Priority',
}

// Wrapper component for message grouping that handles special readStatus case
// eslint-disable-next-line react-refresh/only-export-components
function MessageNodeGroupingComponent(props: {
  nodes?: Message[]
  tasks?: Message[]
  projects?: Message[]
  messages?: Message[]
  groupBy: MessageGroupByOption
  className?: string
  onMessageSelect?: (message: Message) => void
  selectedMessageId?: string
  searchTerm?: string
}) {
  const {
    messages,
    nodes,
    tasks,
    projects,
    groupBy,
    className = '',
    onMessageSelect,
    selectedMessageId,
    searchTerm = MESSAGE_GROUPING_DEFAULTS.searchTerm,
  } = props

  const { user } = useAuth()

  // Handle special grouping cases that need user context
  const processedMessages = useMemo(() => {
    // Use the appropriate data prop, with messages taking priority
    const items = messages || nodes || tasks || projects || []

    if (groupBy === 'readStatus' && user) {
      // For readStatus grouping, we need to handle this specially
      // since the Grouping component doesn't have access to user context
      const readMessages: Message[] = []
      const unreadMessages: Message[] = []

      items.forEach((message) => {
        const isRead = message.readBy.some((read) => read.userId === user.id)
        if (isRead) {
          readMessages.push(message)
        } else {
          unreadMessages.push(message)
        }
      })

      // Return messages grouped by read status
      return [
        { groupName: 'Read', messages: readMessages },
        { groupName: 'Unread', messages: unreadMessages },
      ]
    }

    return null // Let the Grouping component handle other cases
  }, [messages, nodes, tasks, projects, groupBy, user])

  if (processedMessages) {
    // Handle readStatus grouping manually
    return (
      <div className={`space-y-6 ${className}`}>
        {processedMessages.map(({ groupName, messages: groupMessages }) => (
          <Card key={groupName}>
            <div className="border-b bg-muted/50 px-6 py-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{groupName}</h2>
                <span className="text-sm text-muted-foreground">
                  {groupMessages.length} {groupMessages.length === 1 ? 'message' : 'messages'}
                </span>
              </div>
            </div>
            <CardContent className="p-4">
              <MessageInbox
                onMessageSelect={onMessageSelect}
                selectedMessageId={selectedMessageId}
                preFilteredMessages={groupMessages}
                searchTerm={searchTerm}
              />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <Grouping
      items={messages || nodes || tasks || projects || []}
      groupBy={groupBy}
      config={GROUPING_CONFIGS.message}
      className={className}
      renderList={(groupMessages: Message[]) => (
        <MessageInbox
          onMessageSelect={onMessageSelect}
          selectedMessageId={selectedMessageId}
          preFilteredMessages={groupMessages}
          searchTerm={searchTerm}
        />
      )}
    />
  )
}

// Wrapper component for message grouping selector using the generic GroupingSelector
// eslint-disable-next-line react-refresh/only-export-components
function MessageGroupingSelectorComponent({
  value,
  onChange,
  className,
}: {
  value: MessageGroupByOption
  onChange: (value: MessageGroupByOption) => void
  className?: string
}) {
  return (
    <GroupingSelector
      value={value}
      onChange={onChange}
      options={MESSAGE_GROUPING_OPTIONS}
      optionLabels={MESSAGE_GROUPING_OPTION_LABELS}
      className={className}
    />
  )
}

// Document grouping options and labels
const DOCUMENT_GROUPING_OPTIONS: DocumentGroupByOption[] = ['none', 'type', 'creator', 'workspace']

const DOCUMENT_GROUPING_OPTION_LABELS: Record<DocumentGroupByOption, string> = {
  none: 'No Grouping',
  type: 'Group by Type',
  creator: 'Group by Creator',
  workspace: 'Group by Workspace',
}

// Wrapper component for document grouping using the generic Grouping component
// eslint-disable-next-line react-refresh/only-export-components
function DocumentNodeGroupingComponent(props: {
  nodes?: Document[]
  documents?: Document[]
  groupBy: DocumentGroupByOption
  className?: string
  showType?: boolean
  showTypeIcon?: boolean
  showDescription?: boolean
  showWorkspace?: boolean
  showCreator?: boolean
  showCreatorName?: boolean
  showDate?: boolean
  showSharedCount?: boolean
  maxVisible?: number
}) {
  const {
    documents,
    nodes,
    groupBy = 'none',
    className = '',
    showType = DOCUMENT_GROUPING_DEFAULTS.showType,
    showTypeIcon = DOCUMENT_GROUPING_DEFAULTS.showTypeIcon,
    showDescription = DOCUMENT_GROUPING_DEFAULTS.showDescription,
    showWorkspace = DOCUMENT_GROUPING_DEFAULTS.showWorkspace,
    showCreator = DOCUMENT_GROUPING_DEFAULTS.showCreator,
    showCreatorName = DOCUMENT_GROUPING_DEFAULTS.showCreatorName,
    showDate = DOCUMENT_GROUPING_DEFAULTS.showDate,
    showSharedCount = DOCUMENT_GROUPING_DEFAULTS.showSharedCount,
    maxVisible,
  } = props

  // Use the appropriate data prop, with documents taking priority
  const items = documents || nodes || []

  return (
    <Grouping
      items={items}
      groupBy={groupBy}
      config={GROUPING_CONFIGS.document}
      className={className}
      renderList={(groupDocuments: Document[]) => (
        <NodeList
          items={groupDocuments}
          context="document"
          config={{
            showType,
            showTypeIcon,
            showDescription,
            showWorkspace,
            showCreator,
            showCreatorName,
            showDate,
            showSharedCount,
            maxVisible,
          }}
        />
      )}
    />
  )
}

// Wrapper component for document grouping selector using the generic GroupingSelector
// eslint-disable-next-line react-refresh/only-export-components
function DocumentGroupingSelectorComponent({
  value,
  onChange,
  className,
}: {
  value: DocumentGroupByOption
  onChange: (value: DocumentGroupByOption) => void
  className?: string
}) {
  return (
    <GroupingSelector
      value={value}
      onChange={onChange}
      options={DOCUMENT_GROUPING_OPTIONS}
      optionLabels={DOCUMENT_GROUPING_OPTION_LABELS}
      className={className}
    />
  )
}

// Workspace grouping options and labels
const WORKSPACE_GROUPING_OPTIONS: WorkspaceGroupByOption[] = [
  'none',
  'creator',
  'hasUsers',
  'hasProjects',
  'hasTasks',
]

const WORKSPACE_GROUPING_OPTION_LABELS: Record<WorkspaceGroupByOption, string> = {
  none: 'No Grouping',
  creator: 'Group by Creator',
  hasUsers: 'Group by Team Members',
  hasProjects: 'Group by Projects',
  hasTasks: 'Group by Tasks',
}

// Wrapper component for workspace grouping using the generic NodeGrouping
// eslint-disable-next-line react-refresh/only-export-components
function WorkspaceNodeGroupingComponent(props: {
  nodes?: Workspace[]
  tasks?: Workspace[]
  projects?: Workspace[]
  messages?: Workspace[]
  groupBy: WorkspaceGroupByOption
  className?: string
}) {
  return (
    <NodeGrouping
      {...props}
      nodeType="workspace"
      context="workspace"
      config={{
        showDescription: true,
        showCreator: false,
        showUserCount: false,
        showRelationshipCounts: true,
        hoverMode: true,
        avatarSize: 'md' as const,
        textSizeName: 'xl',
      }}
    />
  )
}

// Wrapper component for workspace grouping selector using the generic GroupingSelector
// eslint-disable-next-line react-refresh/only-export-components
function WorkspaceGroupingSelectorComponent({
  value,
  onChange,
  className,
}: {
  value: WorkspaceGroupByOption
  onChange: (value: WorkspaceGroupByOption) => void
  className?: string
}) {
  return (
    <GroupingSelector
      value={value}
      onChange={onChange}
      options={WORKSPACE_GROUPING_OPTIONS}
      optionLabels={WORKSPACE_GROUPING_OPTION_LABELS}
      className={className}
    />
  )
}

// Wrapper component for project grouping using the generic NodeGrouping
// eslint-disable-next-line react-refresh/only-export-components
function ProjectNodeGroupingComponent(props: {
  nodes?: Project[]
  tasks?: Project[]
  projects?: Project[]
  messages?: Project[]
  groupBy: ProjectGroupByOption
  className?: string
}) {
  return (
    <NodeGrouping
      {...props}
      nodeType="project"
      context="project"
      config={{
        showStatus: true,
        showPriority: true,
        showHorizon: false,
        showWorkspace: true,
        showUserCount: true,
        showDescription: false,
        showCreator: false,
        avatarSize: 'md' as const,
      }}
    />
  )
}

// Wrapper component for task grouping using the generic NodeGrouping
// eslint-disable-next-line react-refresh/only-export-components
function TaskNodeGroupingComponent(props: {
  nodes?: Task[]
  tasks?: Task[]
  projects?: Task[]
  messages?: Task[]
  groupBy: TaskGroupByOption
  className?: string
}) {
  return (
    <NodeGrouping
      {...props}
      nodeType="task"
      context="task"
      config={{
        showStatus: true,
        showPriority: true,
        showHorizon: true,
        showType: false,
        showDescription: true,
        showWorkspace: true,
        showAssignee: true,
        showDueDate: true,
        showSubtasks: true,
        statusSize: 'sm' as const,
      }}
    />
  )
}

// Project grouping options and labels
const PROJECT_GROUPING_OPTIONS: ProjectGroupByOption[] = [
  'none',
  'status',
  'priority',
  'horizon',
  'workspace',
  'creator',
]

const PROJECT_GROUPING_OPTION_LABELS: Record<ProjectGroupByOption, string> = {
  none: 'No Grouping',
  status: 'Group by Status',
  priority: 'Group by Priority',
  horizon: 'Group by Horizon',
  workspace: 'Group by Workspace',
  creator: 'Group by Creator',
}

// Wrapper component for project grouping selector using the generic GroupingSelector
// eslint-disable-next-line react-refresh/only-export-components
function ProjectGroupingSelectorComponent({
  value,
  onChange,
  className,
}: {
  value: ProjectGroupByOption
  onChange: (value: ProjectGroupByOption) => void
  className?: string
}) {
  return (
    <GroupingSelector
      value={value}
      onChange={onChange}
      options={PROJECT_GROUPING_OPTIONS}
      optionLabels={PROJECT_GROUPING_OPTION_LABELS}
      className={className}
    />
  )
}

// Task grouping options and labels
const TASK_GROUPING_OPTIONS: TaskGroupByOption[] = [
  'none',
  'type',
  'status',
  'priority',
  'horizon',
  'assignee',
  'workspace',
  'project',
]

const TASK_GROUPING_OPTION_LABELS: Record<TaskGroupByOption, string> = {
  none: 'No Grouping',
  type: 'Group by Type',
  status: 'Group by Status',
  priority: 'Group by Priority',
  horizon: 'Group by Horizon',
  assignee: 'Group by Assignee',
  workspace: 'Group by Workspace',
  project: 'Group by Project',
}

// Wrapper component for task grouping selector using the generic GroupingSelector
// eslint-disable-next-line react-refresh/only-export-components
function TaskGroupingSelectorComponent({
  value,
  onChange,
  className,
}: {
  value: TaskGroupByOption
  onChange: (value: TaskGroupByOption) => void
  className?: string
}) {
  return (
    <GroupingSelector
      value={value}
      onChange={onChange}
      options={TASK_GROUPING_OPTIONS}
      optionLabels={TASK_GROUPING_OPTION_LABELS}
      className={className}
    />
  )
}

// Projects configuration
export const projectsListConfig: NodeListConfig<
  Project,
  ProjectsFiltersState,
  ProjectGroupByOption
> = {
  // Node identification
  nodeName: 'Project',
  nodeNamePlural: 'Projects',

  // Page configuration
  title: 'Projects',
  description: 'Manage and track your projects',
  searchPlaceholder: 'Search projects by name, description, or workspace...',
  createAction: {
    label: 'New Project',
    icon: Plus,
    href: '/projects/new',
  },

  // Tab configuration
  tabs: projectsPageTabs,
  defaultTab: 'my-incomplete',

  // Filter configuration
  FilterComponent: BaseFiltersContainer,
  defaultFilters: {
    status: 'all',
    priority: 'all',
    horizon: 'all',
    creator: 'all',
    workspace: 'all',
    user: 'all',
    createdDate: 'all',
    updatedDate: 'all',
    hasTasks: 'all',
    hasMessages: 'all',
    hasDocuments: 'all',
  } as ProjectsFiltersState,

  // Grouping configuration
  GroupingComponent: ProjectNodeGroupingComponent,
  defaultGroupBy: 'none' as ProjectGroupByOption,
  GroupingSelectorComponent: ProjectGroupingSelectorComponent,

  // Data processing
  filterNodes: (
    projects: Project[],
    activeTab: string | undefined,
    filters: ProjectsFiltersState,
    searchQuery: string,
    user?: { id: string; role: string }
  ): Project[] => {
    if (!user) return []

    // Apply tab-based filtering first
    const tabFilteredProjects = projects.filter((project) => {
      const isProjectAssociatedWithUser =
        project.creator.id === user.id || project.users.some((u) => u.id === user.id)

      switch (activeTab) {
        case 'my-incomplete':
          return (
            isProjectAssociatedWithUser && project.status !== 'Done' && project.status !== 'Draft'
          )
        case 'all-incomplete':
          if (user.role === 'SuperAdmin') {
            return project.status !== 'Done' && project.status !== 'Draft'
          } else {
            return (
              isProjectAssociatedWithUser && project.status !== 'Done' && project.status !== 'Draft'
            )
          }
        case 'my-drafts':
          return isProjectAssociatedWithUser && project.status === 'Draft'
        default:
          return true
      }
    })

    // Apply comprehensive filtering
    return tabFilteredProjects.filter((project) => {
      // Helper function to check if a date falls within a time range
      const isDateInRange = (date: Date, range: string): boolean => {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const projectDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

        switch (range) {
          case 'today':
            return projectDate.getTime() === today.getTime()
          case 'week': {
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
            return projectDate >= weekAgo
          }
          case 'month': {
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
            return projectDate >= monthAgo
          }
          case 'older': {
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
            return projectDate < monthAgo
          }
          default:
            return true
        }
      }

      // Search filter
      const matchesSearch =
        project.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.workspace.name?.toLowerCase().includes(searchQuery.toLowerCase())

      // Status filter
      const matchesStatus = filters.status === 'all' || project.status === filters.status

      // Priority filter
      const matchesPriority = filters.priority === 'all' || project.priority === filters.priority

      // Horizon filter
      const matchesHorizon = filters.horizon === 'all' || project.horizon === filters.horizon

      // Creator filter
      const matchesCreator = filters.creator === 'all' || project.creator.id === filters.creator

      // Workspace filter
      const matchesWorkspace =
        filters.workspace === 'all' || project.workspace.id === filters.workspace

      // User filter (team member)
      const matchesUser = filters.user === 'all' || project.users.some((u) => u.id === filters.user)

      // Created date filter
      const matchesCreatedDate =
        filters.createdDate === 'all' || isDateInRange(project.createdAt, filters.createdDate)

      // Updated date filter
      const matchesUpdatedDate =
        filters.updatedDate === 'all' || isDateInRange(project.updatedAt, filters.updatedDate)

      // Has tasks filter
      const matchesHasTasks =
        filters.hasTasks === 'all' ||
        (filters.hasTasks === 'yes' && project.tasks.length > 0) ||
        (filters.hasTasks === 'no' && project.tasks.length === 0)

      // Has messages filter
      const matchesHasMessages =
        filters.hasMessages === 'all' ||
        (filters.hasMessages === 'yes' && project.messages.length > 0) ||
        (filters.hasMessages === 'no' && project.messages.length === 0)

      // Has documents filter
      const matchesHasDocuments =
        filters.hasDocuments === 'all' ||
        (filters.hasDocuments === 'yes' && project.documents.length > 0) ||
        (filters.hasDocuments === 'no' && project.documents.length === 0)

      return (
        matchesSearch &&
        matchesStatus &&
        matchesPriority &&
        matchesHorizon &&
        matchesCreator &&
        matchesWorkspace &&
        matchesUser &&
        matchesCreatedDate &&
        matchesUpdatedDate &&
        matchesHasTasks &&
        matchesHasMessages &&
        matchesHasDocuments
      )
    })
  },

  // Component props
  filterComponentProps: {
    // These will be passed from the page component
    availableWorkspaces: [] as Workspace[],
    availableUsers: [] as User[],
    filterConfigs: PROJECTS_FILTER_CONFIGS,
    gridCols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  },

  // Specify which prop name to use for the data array
  dataPropName: 'projects',
}

// Sorting function for tasks
export const sortNodes = (tasks: Task[]): Task[] => {
  return [...tasks].sort((a, b) => {
    // Helper function to check if a task is overdue
    const isOverdue = (task: Task): boolean => {
      if (!task.dueDate) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const dueDate = new Date(task.dueDate)
      dueDate.setHours(0, 0, 0, 0)
      return dueDate < today
    }

    // Helper function to get sort priority (lower number = higher priority)
    const getSortPriority = (task: Task): number => {
      if (isOverdue(task)) return 0 // Overdue tasks first
      if (task.dueDate) return 1 // Tasks with due date second
      return 2 // Tasks without due date last
    }

    // Helper function to compare due dates
    const compareDueDates = (a: Task, b: Task): number => {
      if (!a.dueDate && !b.dueDate) return 0
      if (!a.dueDate) return 1
      if (!b.dueDate) return -1

      const aDate = new Date(a.dueDate)
      const bDate = new Date(b.dueDate)
      return aDate.getTime() - bDate.getTime() // Earlier dates first
    }

    // First, sort by priority (overdue, has due date, no due date)
    const aPriority = getSortPriority(a)
    const bPriority = getSortPriority(b)

    if (aPriority !== bPriority) {
      return aPriority - bPriority
    }

    // Then, for tasks with due dates, sort by due date (earliest first)
    if (aPriority === 1) {
      return compareDueDates(a, b)
    }

    // For tasks without due dates or overdue tasks, maintain original order
    return 0
  })
}

// Tasks configuration
export const tasksListConfig: NodeListConfig<Task, TasksFiltersState, TaskGroupByOption> = {
  // Node identification
  nodeName: 'Task',
  nodeNamePlural: 'Tasks',

  // Page configuration
  title: 'Tasks',
  description: 'Track and manage your tasks',
  searchPlaceholder: 'Search tasks...',
  createAction: {
    label: 'New Task',
    icon: Plus,
    href: '/tasks/new',
  },

  // Tab configuration
  tabs: tasksPageTabs,
  defaultTab: 'my-assigned',

  // Filter configuration
  FilterComponent: BaseFiltersContainer,
  defaultFilters: {
    taskType: 'all',
    status: 'all',
    priority: 'all',
    horizon: 'all',
    assignee: 'all',
    dueDate: 'all',
    isDependency: 'all',
  } as TasksFiltersState,

  // Grouping configuration
  GroupingComponent: TaskNodeGroupingComponent,
  defaultGroupBy: 'none' as TaskGroupByOption,
  GroupingSelectorComponent: TaskGroupingSelectorComponent,

  // Data processing
  sortNodes,

  filterNodes: (
    tasks: Task[],
    activeTab: string | undefined,
    filters: TasksFiltersState,
    searchQuery: string,
    user?: { id: string; role: string }
  ): Task[] => {
    if (!user) return []

    // Apply tab-based filtering first
    let filteredTasks = tasks.filter((task) => {
      const isTaskAssociatedWithUser = (task: Task): boolean => {
        return (
          task.assignee?.id === user.id ||
          task.creator.id === user.id ||
          task.subtasks?.some((subtask) => subtask.assignee?.id === user.id)
        )
      }

      switch (activeTab) {
        case 'my-assigned':
          return (
            task.assignee?.id === user.id &&
            (task.status === 'To Do' || task.status === 'In Progress')
          )
        case 'my-associated':
          return (
            isTaskAssociatedWithUser(task) &&
            (task.status === 'To Do' || task.status === 'In Progress')
          )
        case 'my-drafts':
          return isTaskAssociatedWithUser(task) && task.status === 'Draft'
        case 'all':
          if (user.role === 'SuperAdmin') {
            return task.status === 'To Do' || task.status === 'In Progress'
          } else {
            return (
              isTaskAssociatedWithUser(task) &&
              (task.status === 'To Do' || task.status === 'In Progress')
            )
          }
        default:
          return true
      }
    })

    // Helper: Check if a task is a dependency of any other task
    const isTaskDependency = (taskId: string): boolean => {
      return tasks.some((task) => task.dependencies?.some((dep) => dep.id === taskId))
    }

    // Helper: Check due date filters
    const matchesDueDateFilter = (task: Task): boolean => {
      if (filters.dueDate === 'all') return true
      if (filters.dueDate === 'no-date') return !task.dueDate

      if (!task.dueDate) return false

      const dueDate = new Date(task.dueDate)
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      if (filters.dueDate === 'overdue') {
        return dueDate < today
      } else if (filters.dueDate === 'today') {
        const tomorrow = new Date(today)
        tomorrow.setDate(tomorrow.getDate() + 1)
        return dueDate >= today && dueDate < tomorrow
      } else if (filters.dueDate === 'week') {
        const nextWeek = new Date(today)
        nextWeek.setDate(nextWeek.getDate() + 7)
        return dueDate >= today && dueDate <= nextWeek
      } else if (filters.dueDate === 'month') {
        const nextMonth = new Date(today)
        nextMonth.setMonth(nextMonth.getMonth() + 1)
        return dueDate >= today && dueDate <= nextMonth
      }

      return true
    }

    // Apply actual filters from filterState
    filteredTasks = filteredTasks.filter((task) => {
      // Task type filter
      if (filters.taskType !== 'all' && task.type !== filters.taskType) return false

      // Status filter
      if (filters.status !== 'all' && task.status !== filters.status) return false

      // Priority filter
      if (filters.priority !== 'all' && task.priority !== filters.priority) return false

      // Horizon filter
      if (filters.horizon !== 'all' && task.horizon !== filters.horizon) return false

      // Assignee filter
      if (filters.assignee !== 'all') {
        if (filters.assignee === 'assigned' && !task.assignee) return false
        if (filters.assignee === 'unassigned' && task.assignee) return false
        if (
          filters.assignee !== 'assigned' &&
          filters.assignee !== 'unassigned' &&
          task.assignee?.id !== filters.assignee
        )
          return false
      }

      // Due date filter
      if (!matchesDueDateFilter(task)) return false

      // Dependency filter
      if (filters.isDependency !== 'all') {
        const isDep = isTaskDependency(task.id)
        if (filters.isDependency === 'yes' && !isDep) return false
        if (filters.isDependency === 'no' && isDep) return false
      }

      return true
    })

    // Apply search filter
    if (searchQuery) {
      filteredTasks = filteredTasks.filter((task) => {
        return (
          task.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.workspace.name?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })
    }

    // Apply sorting after filtering
    return sortNodes(filteredTasks)
  },

  // Component props
  filterComponentProps: {
    // These will be passed from the page component
    availableUsers: [] as User[],
    filterConfigs: TASKS_FILTER_CONFIGS,
    gridCols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  },

  // Specify which prop name to use for the data array
  dataPropName: 'tasks',

  // Empty state configuration
  emptyStateIcon: AlertCircle,
}

// Messages configuration
export const messagesListConfig: NodeListConfig<
  Message,
  MessagesFiltersState,
  MessageGroupByOption
> = {
  // Node identification
  nodeName: 'Message',
  nodeNamePlural: 'Messages',

  // Page configuration
  title: 'Messages',
  description: 'Your message inbox',
  searchPlaceholder: 'Search messages...',
  createAction: {
    label: 'New Message',
    icon: Plus,
    href: '/messages/new',
  },

  // Tab configuration
  tabs: [
    { value: 'unread', label: 'Unread Messages', icon: projectsPageTabs[0].icon },
    { value: 'open-projects', label: 'Ongoing Work Messages', icon: projectsPageTabs[0].icon },
    { value: 'all', label: 'All Messages', icon: projectsPageTabs[0].icon },
    { value: 'drafts', label: 'Draft Messages', icon: projectsPageTabs[0].icon },
    { value: 'archived', label: 'Archived Messages', icon: projectsPageTabs[0].icon },
  ],
  defaultTab: 'unread',

  // Filter configuration
  FilterComponent: BaseFiltersContainer,
  defaultFilters: {
    status: 'all',
    creator: 'all',
    workspace: 'all',
    user: 'all',
    project: 'all',
    task: 'all',
    document: 'all',
    directMessage: 'all',
    createdDate: 'all',
    updatedDate: 'all',
    readStatus: 'all',
    hasReplies: 'all',
    priority: 'all',
  } as MessagesFiltersState,

  // Grouping configuration
  GroupingComponent: MessageNodeGroupingComponent,
  defaultGroupBy: 'none' as MessageGroupByOption,
  GroupingSelectorComponent: MessageGroupingSelectorComponent,

  // Data processing
  filterNodes: (
    messages: Message[],
    activeTab: string | undefined,
    filters: MessagesFiltersState,
    searchQuery: string,
    user?: { id: string; role: string }
  ): Message[] => {
    if (!user) return []

    // Helper function to check if a date falls within a time range
    const _isDateInRange = (date: Date, range: string): boolean => {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

      switch (range) {
        case 'today':
          return messageDate.getTime() === today.getTime()
        case 'week': {
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
          return messageDate >= weekAgo
        }
        case 'month': {
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
          return messageDate >= monthAgo
        }
        case 'older': {
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
          return messageDate < monthAgo
        }
        default:
          return true
      }
    }

    // Apply tab-based filtering first (similar to useMessagesFilters logic)
    let filteredMessages = messages.filter((message) => {
      switch (activeTab) {
        case 'unread':
          // Messages that haven't been read by current user
          return !message.readBy.some((read) => read.userId === user.id)
        case 'open-projects':
          // Messages related to ongoing projects (not archived or draft)
          return message.status !== 'Archived' && message.status !== 'Draft'
        case 'drafts':
          // Draft messages
          return message.status === 'Draft'
        case 'archived':
          // Archived messages
          return message.status === 'Archived'
        case 'all':
        default:
          // All messages except drafts
          return message.status !== 'Draft'
      }
    })

    // Apply comprehensive filtering from filterState
    filteredMessages = filteredMessages.filter((message) => {
      // Status filter
      if (filters.status !== 'all' && message.status !== filters.status) return false

      // Creator filter
      if (filters.creator !== 'all' && message.creator.id !== filters.creator) return false

      // Workspace filter
      if (filters.workspace !== 'all' && message.workspace?.id !== filters.workspace) return false

      // User filter (participants)
      if (filters.user !== 'all') {
        const hasUser = message.users.some((participant) => participant.id === filters.user)
        if (!hasUser) return false
      }

      // Project filter
      if (filters.project !== 'all' && !message.projects.some((p) => p.id === filters.project))
        return false

      // Task filter
      if (filters.task !== 'all' && !message.tasks.some((t) => t.id === filters.task)) return false

      // Document filter
      if (filters.document !== 'all' && !message.documents.some((d) => d.id === filters.document))
        return false

      // Direct message filter
      if (filters.directMessage !== 'all') {
        const isDirect = message.directMessage
        if (filters.directMessage === 'yes' && !isDirect) return false
        if (filters.directMessage === 'no' && isDirect) return false
      }

      // Created date filter
      if (filters.createdDate !== 'all' && !_isDateInRange(message.createdAt, filters.createdDate))
        return false

      // Updated date filter
      if (filters.updatedDate !== 'all' && !_isDateInRange(message.updatedAt, filters.updatedDate))
        return false

      // Read status filter
      if (filters.readStatus !== 'all') {
        const isRead = message.readBy.some((read) => read.userId === user.id)
        if (filters.readStatus === 'read' && !isRead) return false
        if (filters.readStatus === 'unread' && isRead) return false
      }

      // Has replies filter
      if (filters.hasReplies !== 'all') {
        const hasReplies = message.messages && message.messages.length > 0
        if (filters.hasReplies === 'yes' && !hasReplies) return false
        if (filters.hasReplies === 'no' && hasReplies) return false
      }

      // Priority filter (messages don't have priority, but we can filter by status as proxy)
      if (filters.priority !== 'all') {
        // For now, we'll skip this filter since messages don't have priority
        // In a real implementation, you might add priority to messages or use status as proxy
      }

      return true
    })

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      filteredMessages = filteredMessages.filter((message) => {
        return (
          message.name.toLowerCase().includes(searchLower) ||
          message.description.toLowerCase().includes(searchLower) ||
          message.creator.name.toLowerCase().includes(searchLower)
        )
      })
    }

    // Sort messages reverse-chronologically by created date (newest first)
    filteredMessages = [...filteredMessages].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

    return filteredMessages
  },

  // Component props
  filterComponentProps: {
    // These will be passed from the page component
    availableWorkspaces: [] as Workspace[],
    availableUsers: [] as User[],
    availableProjects: [] as Project[],
    availableTasks: [] as Task[],
    availableDocuments: [] as Document[],
    currentUserId: '',
    filterConfigs: MESSAGES_FILTER_CONFIGS,
    gridCols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  },
  groupingComponentProps: {
    onMessageSelect: () => {}, // Will be overridden in the page
    selectedMessageId: undefined,
    // searchTerm: '', // Search is handled in filterNodes, no need to pass it here
  },

  // Specify which prop name to use for the data array
  dataPropName: 'messages',
}

// Documents configuration
export const documentsListConfig: NodeListConfig<Document, DocumentFilters, DocumentGroupByOption> =
  {
    // Node identification
    nodeName: 'document',
    nodeNamePlural: 'documents',

    // Page configuration
    title: 'Documents',
    description: 'Manage and organize your documents',
    searchPlaceholder: 'Search documents by name, description, or workspace...',
    createAction: {
      label: 'Upload Document',
      icon: Plus,
      href: '/documents/new',
    },

    // Tab configuration
    tabs: documentsPageTabs,
    defaultTab: 'all-documents',

    // Filter configuration
    FilterComponent: BaseFiltersContainer,
    defaultFilters: {
      type: 'all',
    } as DocumentFilters,

    // Grouping configuration
    GroupingComponent: DocumentNodeGroupingComponent,
    defaultGroupBy: 'none' as DocumentGroupByOption,
    GroupingSelectorComponent: DocumentGroupingSelectorComponent,

    // Data processing - handles search, type filtering, and tab filtering
    filterNodes: (
      documents: Document[],
      activeTab: string | undefined,
      filters: DocumentFilters,
      searchQuery: string,
      user?: { id: string; role: string }
    ): Document[] => {
      if (!user) return []

      // Apply tab-based filtering first
      let filteredDocuments = documents.filter((doc) => {
        const isDocumentAssociatedWithUser =
          doc.creator.id === user.id ||
          doc.users.some((u) => u.id === user.id) ||
          doc.workspace.creator.id === user.id ||
          doc.workspace.users.some((u) => u.id === user.id)

        // Helper function to check if document is associated with ongoing work
        const isDocumentInOngoingWork = (): boolean => {
          // Check if document is associated with incomplete projects
          const hasIncompleteProjects = doc.projects.some(
            (project) => project.status !== 'Done' && project.status !== 'Draft'
          )

          // Check if document is associated with incomplete tasks
          const hasIncompleteTasks = doc.tasks.some(
            (task) => task.status !== 'Done' && task.status !== 'Draft'
          )

          return hasIncompleteProjects || hasIncompleteTasks
        }

        switch (activeTab) {
          case 'my-documents':
            return doc.creator.id === user.id
          case 'ongoing-work':
            return isDocumentInOngoingWork() && isDocumentAssociatedWithUser
          case 'all-documents':
          default:
            // SuperAdmins see all documents, others see only documents they have access to
            if (user.role === 'SuperAdmin') {
              return true
            } else {
              return isDocumentAssociatedWithUser
            }
        }
      })

      // Apply search filtering
      if (searchQuery) {
        filteredDocuments = filteredDocuments.filter(
          (doc) =>
            doc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            doc.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            doc.workspace.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            doc.creator.name?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      }

      // Apply type filtering
      filteredDocuments = filteredDocuments.filter(
        (doc) => filters.type === 'all' || doc.type === filters.type
      )

      return filteredDocuments
    },

    // Component props
    filterComponentProps: {
      // These will be passed from the page component
      filterConfigs: DOCUMENT_FILTER_CONFIGS,
      gridCols: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    },
    groupingComponentProps: {
      showType: true,
      showTypeIcon: true,
      showDescription: true,
      showWorkspace: false,
      showCreator: true,
      showCreatorName: true,
      showDate: true,
      showSharedCount: false,
    },

    // Specify which prop name to use for the data array
    dataPropName: 'nodes' as const,

    // Empty state configuration
    emptyStateIcon: Plus,
    emptyStateTitle: 'No documents found',
    emptyStateDescription: 'Get started by uploading your first document',
  }

// Default filter state for workspaces
export const DEFAULT_WORKSPACE_FILTERS: WorkspacesFiltersState = {
  creator: 'all',
  hasUsers: 'all',
  hasProjects: 'all',
  hasTasks: 'all',
  hasMessages: 'all',
  hasDocuments: 'all',
}

// Default grouping option for workspaces
export const DEFAULT_WORKSPACE_GROUP_BY: WorkspaceGroupByOption = 'none'

// Filter function for workspaces
export function filterWorkspaces(
  workspaces: Workspace[],
  _activeTab: string | undefined,
  filters: WorkspacesFiltersState,
  searchQuery: string,
  user?: { id: string; role: string }
): Workspace[] {
  if (!user) return []

  // Apply search filtering
  const searchFilteredWorkspaces = workspaces.filter((workspace) => {
    if (
      searchQuery &&
      !workspace.name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !workspace.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false
    }

    // Creator filter
    if (filters.creator !== 'all' && workspace.creator.id !== filters.creator) {
      return false
    }

    // Has users filter
    if (filters.hasUsers !== 'all') {
      const hasUsers = workspace.users.length > 0
      if (filters.hasUsers === 'yes' && !hasUsers) return false
      if (filters.hasUsers === 'no' && hasUsers) return false
    }

    // Has projects filter
    if (filters.hasProjects !== 'all') {
      const hasProjects = workspace.projects.length > 0
      if (filters.hasProjects === 'yes' && !hasProjects) return false
      if (filters.hasProjects === 'no' && hasProjects) return false
    }

    // Has tasks filter
    if (filters.hasTasks !== 'all') {
      const hasTasks = workspace.tasks.length > 0
      if (filters.hasTasks === 'yes' && !hasTasks) return false
      if (filters.hasTasks === 'no' && hasTasks) return false
    }

    // Has messages filter
    if (filters.hasMessages !== 'all') {
      const hasMessages = workspace.messages.length > 0
      if (filters.hasMessages === 'yes' && !hasMessages) return false
      if (filters.hasMessages === 'no' && hasMessages) return false
    }

    // Has documents filter
    if (filters.hasDocuments !== 'all') {
      const hasDocuments = workspace.documents.length > 0
      if (filters.hasDocuments === 'yes' && !hasDocuments) return false
      if (filters.hasDocuments === 'no' && hasDocuments) return false
    }

    return true
  })

  return searchFilteredWorkspaces
}

// Workspaces configuration
export const workspacesListConfig: NodeListConfig<
  Workspace,
  WorkspacesFiltersState,
  WorkspaceGroupByOption
> = {
  // Node identification
  nodeName: 'workspace',
  nodeNamePlural: 'workspaces',

  // Page configuration
  title: 'Workspaces',
  description: 'Manage your workspaces',
  searchPlaceholder: 'Search workspaces by name or description...',
  createAction: {
    label: 'New Workspace',
    icon: Plus,
    href: '/workspaces/new',
  },

  // Tab configuration - workspaces don't use tabs in the current implementation
  tabs: [],
  defaultTab: undefined,

  // Filter configuration
  FilterComponent: BaseFiltersContainer,
  defaultFilters: DEFAULT_WORKSPACE_FILTERS,

  // Grouping configuration
  GroupingComponent: WorkspaceNodeGroupingComponent,
  defaultGroupBy: DEFAULT_WORKSPACE_GROUP_BY,
  GroupingSelectorComponent: WorkspaceGroupingSelectorComponent,

  // Data processing - handles search and filtering
  filterNodes: filterWorkspaces,

  // Component props
  filterComponentProps: {
    // These will be passed from the page component
    availableUsers: [] as User[],
    filterConfigs: WORKSPACES_FILTER_CONFIGS,
  },

  // Specify which prop name to use for the data array
  dataPropName: 'nodes' as const,

  // Empty state configuration
  emptyStateIcon: Boxes,
  emptyStateTitle: 'No workspaces found',
  emptyStateDescription: 'Create your first workspace to get started',
}
